import { Command } from 'pos/common';
import * as ProductComposition from './ProductComposition';
import * as SalesStore from '../SalesStore';

import getMenuQuantity from './getMenuQuantity';
import { ProductForSaleDTO, Permission, Res, SoldProductType } from 'core/api';
import { Result, SessionProvider } from 'core/infrastructure';
import { Preconditions } from 'core/infrastructure-next';
import { ThemeManager } from 'pos/ui';
import { CompositionParams, getCompositionSteps, createCompositionStateFor, createEmptyCompositionStateFor } from './product';
import { Message, Modal } from 'core/ui-next/modal';
import { getCompositionResultsFrom, ProductWizardScreen } from './product';
import { RestaurantFacade } from 'pos/facades';

export function getSoldProductType(compositionType: ProductComposition.CompositionType) {
	switch (compositionType.type) {
		case 'menu-header': return SoldProductType.MenuHeader;
		case 'menu-item': return SoldProductType.MenuItem;
		case 'addin': return compositionType.addinMenuGroupName ? SoldProductType.MenuItem : SoldProductType.Standard;
		case 'standard': return SoldProductType.Standard;
		default: throw Error('CompositionType inválido');
	}
}
async function compose(product: ProductForSaleDTO, compositionType: ProductComposition.MainParams): Promise<Result<ProductComposition.ProductComposition[]>> {

	const steps = getCompositionSteps(product);

	// Si no tengo steps o no tengo formatos, creo una composición con el producto ya seleccionado. Si no, creo una vacía sin
	// producto seleccionado
	const compositionResult = steps.length === 0 || !product.HasMoreFormats
		? await createCompositionStateFor(product, compositionType.quantity)
		: Result.ok(createEmptyCompositionStateFor(product, compositionType.quantity));

	if (!compositionResult.ok)
		return Result.error();

	const params: CompositionParams = {
		product,
		groupName: compositionType.type === 'menu-item' ? compositionType.groupName : '',
		preparationOrderId: compositionType.preparationOrderId,
		preparationTypeId: product.PreparationTypeId,
		type: getSoldProductType(compositionType)
	};

	const profile = await RestaurantFacade.getDisplayProfile();

	const stepsCompositionResult = steps.length === 0
		? Result.ok(getCompositionResultsFrom(params, compositionResult.value))
		: await Modal.show(ProductWizardScreen, {
			params: {
				product,
				groupName: compositionType.type === 'menu-item' ? compositionType.groupName : '',
				preparationOrderId: compositionType.preparationOrderId,
				preparationTypeId: product.PreparationTypeId,
				type: getSoldProductType(compositionType)
			},
			initialComposition: compositionResult.value,
			initialSteps: steps,
			profile
		});

	if (!stepsCompositionResult.ok)
		return Result.error();

	const compositions: ProductComposition.ProductComposition[] = stepsCompositionResult.value.map(c => ({
		...c,
		couponCode: product.CouponCode,
		ownerId: compositionType.type === 'menu-item' ? compositionType.ownerId : null,
		seatNumber: SalesStore.getSeatNumber()
	}));

	return Result.ok(compositions);
}

async function composeStandardProduct(product: ProductForSaleDTO, quantity?: number): Promise<Result<ProductComposition.ProductComposition[]>> {
	const preparationOrder = SalesStore.getPreparationOrder();
	return compose(product, { type: 'standard', preparationOrderId: preparationOrder ? preparationOrder.Id : product.PreparationOrderId, quantity: quantity ? quantity : 1 });
}

async function composeMenuHeader(product: ProductForSaleDTO, quantity?: number): Promise<Result<ProductComposition.ProductComposition[]>> {
	const menuQuantity = ThemeManager.isPhone() ? quantity : quantity || 1;
	const menuQuantityResult = menuQuantity ? Result.ok(menuQuantity) : await getMenuQuantity(99);
	if (!menuQuantityResult.ok)
		return Result.error();

	const preparationOrder = SalesStore.getPreparationOrder();
	const compositionResult = await compose(product, { type: 'menu-header', preparationOrderId: preparationOrder ? preparationOrder.Id : product.PreparationOrderId, quantity: menuQuantityResult.value });
	return compositionResult;
}

async function composeMenuItem(product: ProductForSaleDTO, compositionType: ProductComposition.MenuItemParams): Promise<Result<ProductComposition.ProductComposition[]>> {
	const compositionResult = await compose(product, compositionType);
	if (!compositionResult.ok)
		return Result.error();

	return compositionResult;
}

async function composeProduct(product: ProductForSaleDTO, quantity: number | undefined, menuItem?: ProductComposition.MenuItemParams): Promise<Result<ProductComposition.ProductComposition[]>> {

	if (quantity === 0) {
		await Message.show(Res.Msg.MSG_CANNOT_SELL_ZERO_UNIT_OF_PRODUCT(product.Name));
		return Result.error();
	}

	if (quantity !== undefined && quantity < 0 && !SessionProvider.hasPermission(Permission.Ticket_AllowNegativeLines)) {
		await Message.show(Res.Msg.MSG_PERMISSION_DENIED_FOR(Permission.toString(Permission.Ticket_AllowNegativeLines)));
		return Result.error();
	}

	if (product.IsSubFamily || product.IsSubCategory) {
		await Message.show(Res.Msg.MSG_PRODUCT_NOT_SALEABLE_AS_MAIN(product.Name));
		return Result.error();
	}

	if (product.IsMenu)
		return composeMenuHeader(product, quantity);

	if (menuItem && product.IsSoldByWeight) {
		await Message.show(Res.Msg.MSG_PRODUCT_SOLD_BY_WEIGHT_NOT_SALEABLE_IN_MENU(product.Name));
		return Result.error();
	}

	if (menuItem)
		return composeMenuItem(product, menuItem);

	return composeStandardProduct(product, quantity);
}

declare global {
	interface CommandType {
		composeProduct: (product: ProductForSaleDTO, quantity: number | undefined, menuItem?: ProductComposition.MenuItemParams) => Promise<Result<ProductComposition.ProductComposition[]>>;
	}
}

Command.register('composeProduct', Preconditions.ensure(Permission.Ticket_ComposeProduct), composeProduct);

